import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  fruitBaskets,
  getProduct,
  images,
  initialAddresses,
  products,
  serviceAreas,
  shakeProducts,
  timeSlots,
  weekdays,
} from "../data";
import { useApp } from "../store";
import { isOrderModifiable } from "../utils/admin";
import {
  AddressCard,
  Brand,
  DeliveryCard,
  EmptyState,
  PageHeader,
  PrimaryButton,
  ProductCard,
  StatusBadge,
} from "../components/UI";

import "../css/DeliveryDetails.css";
export function DeliveryDetails() {
  const { addresses } = useApp();
  const { id } = useParams();
  const { deliveries, updateSchedule, updateOrderStatus } = useApp();
  const delivery = deliveries.find((d) => d.id === id) || deliveries[0];
  const canModify = isOrderModifiable(delivery);
  const [actionError, setActionError] = useState("");
  const product = getProduct(delivery?.productId);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="page-wrap narrow container-xl">
      <PageHeader title="Delivery details" />
      <div className="detail-status">
        <StatusBadge status={delivery?.status} />
        <h2>
          {delivery?.date}, {delivery?.timeSlot}
        </h2>
        <p>Updates are manually controlled during Phase 1.</p>
      </div>
      <div className="review-card">
        <div className="review-product">
          <img src={product?.image} alt="" />
          <div>
            <span className="card-kicker">Product</span>
            <h2>{product?.name}</h2>
            <p>Quantity: {delivery?.quantity}</p>
          </div>
          <strong>₹{delivery?.amount}</strong>
        </div>
        {[
          ["fa-calendar", "Date", delivery?.fullDate],
          ["fa-clock", "Window", delivery?.timeSlot],
          [
            "fa-location-dot",
            "Address",
            (() => {
              const address = addresses.find(
                (item) => item.id === delivery?.addressId,
              );
              return address
                ? `${address.type || "Address"} · ${address.line}, ${address.area || address.city || "Pune"}`
                : delivery?.addressLine || "Saved address";
            })(),
          ],
          [
            "fa-repeat",
            "Schedule",
            delivery?.recurring
              ? `${delivery.frequency || "Recurring"}${delivery.days?.length ? ` · ${delivery.days.join(", ")}` : ""}`
              : "One time",
          ],
        ].map(([i, a, b]) => (
          <div className="review-row" key={a}>
            <i className={`fa-solid ${i}`} />
            <div>
              <span>{a}</span>
              <strong>{b}</strong>
            </div>
          </div>
        ))}
      </div>
      <div className="schedule-edit-notice mt-3">
        <i className="fa-solid fa-clock" />
        <div>
          <strong>{canModify ? "Changes available" : "Delivery locked"}</strong>
          <span>
            {canModify
              ? "You can modify this delivery while it is more than 24 hours away."
              : "This delivery is inside the 24-hour modification window, so its time, date, address and quantity cannot be changed."}
          </span>
        </div>
      </div>

      {actionError && (
        <div className="alert alert-danger mt-3" role="alert">
          {actionError}
        </div>
      )}

      <div className="action-list">
        {!["Delivered", "Cancelled", "Skipped"].includes(delivery?.status) && (
          <Link
            to={`/schedule/${delivery?.type || product?.type}/${delivery?.productId || product?.id}?editOrderId=${encodeURIComponent(delivery.id)}&editMode=order`}
          >
            <i className="fa-solid fa-pen-to-square" />
            Change this delivery
            <i className="fa-solid fa-chevron-right" />
          </Link>
        )}
        {delivery?.recurring && (
          <Link to={`/subscriptions/${delivery.id}`}>
            <i className="fa-solid fa-calendar-pen" />
            Manage recurring schedule
            <i className="fa-solid fa-chevron-right" />
          </Link>
        )}
        {!["Delivered", "Cancelled", "Skipped"].includes(delivery?.status) && (
          <button
            data-bs-toggle="modal"
            data-bs-target="#skipModal"
            disabled={!canModify}
            title={
              !canModify
                ? "Skipping is allowed only more than 24 hours before delivery."
                : ""
            }
          >
            <i className="fa-solid fa-forward-step" />
            Skip this delivery
            <i className="fa-solid fa-chevron-right" />
          </button>
        )}
        <Link to="/help">
          <i className="fa-regular fa-message" />
          Contact support
          <i className="fa-solid fa-chevron-right" />
        </Link>
      </div>
      <div className="modal fade" id="skipModal" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body p-4 text-center">
              <span className="modal-icon">
                <i className="fa-solid fa-forward-step" />
              </span>
              <h2>Skip {delivery?.date}’s delivery?</h2>
              <p className="text-secondary">
                {delivery?.timeSlot} · {product?.name || "Delivery"}
              </p>
              <button
                className="btn btn-danger w-100"
                data-bs-dismiss="modal"
                onClick={async () => {
                  if (!canModify) {
                    setActionError(
                      "This delivery is inside the 24-hour modification window and cannot be skipped.",
                    );
                    return;
                  }

                  setActionError("");
                  try {
                    if (delivery?.id) {
                      await updateOrderStatus(delivery.id, "Skipped");
                    }
                    if (delivery?.scheduleId) {
                      await updateSchedule(delivery.scheduleId, {
                        lastSkippedDelivery: delivery.id,
                        lastSkippedDate: delivery.deliveryDate || null,
                      });
                    }
                  } catch (err) {
                    setActionError(
                      err?.message ||
                        "Unable to skip this delivery. Please refresh and try again.",
                    );
                  }
                }}
              >
                Skip this delivery
              </button>
              <button
                className="btn btn-soft w-100 mt-2"
                data-bs-dismiss="modal"
              >
                Keep it
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeliveryDetails;
